import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackArrow from '../ui/BackArrow';

const TURRIS_LOGO = '/logo-turris.svg';

export default function TermsOfUseScreen() {
  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FDFBF7' }}>
      {/* Header */}
      <header className="p-4 sm:p-6 flex items-center justify-between sticky top-0 bg-[#FDFBF7] z-10 border-b border-gray-100">
        <BackArrow onClick={goBack} />
        <img src={TURRIS_LOGO} alt="Turris" className="h-8 sm:h-10" />
        <div className="w-10" />
      </header>

      {/* Content */}
      <main className="flex-1 px-4 sm:px-8 lg:px-16 py-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 
            className="text-3xl sm:text-4xl font-bold mb-8 text-center"
            style={{ color: '#1a365d' }}
          >
            Condicions d'Ús
          </h1>

          <div className="prose prose-lg max-w-none" style={{ color: '#333' }}>
            <p className="text-sm text-gray-500 mb-6">
              Última actualització: Abril 2025
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                1. Acceptació de les Condicions
              </h2>
              <p className="mb-4 leading-relaxed">
                En accedir i utilitzar el servei de creació de contes personalitzats de <strong>Turris - El Pa de Sant Jordi</strong>, 
                acceptes aquestes condicions d'ús en la seva totalitat. Si no estàs d'acord amb alguna part d'aquestes 
                condicions, no has d'utilitzar el nostre servei.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                2. Descripció del Servei
              </h2>
              <p className="mb-4 leading-relaxed">
                Turris ofereix un servei gratuït de creació de contes personalitzats de Sant Jordi. El servei inclou:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Generació d'un conte personalitzat amb el nom del nen o nena.</li>
                <li>Visualització prèvia del conte en format digital.</li>
                <li>Descàrrega del conte en format PDF per imprimir.</li>
                <li>Enviament del conte al correu electrònic proporcionat.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                3. Condicions del Servei Gratuït
              </h2>
              <p className="mb-4 leading-relaxed">
                El conte personalitzat s'ofereix de forma gratuïta a canvi de:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Proporcionar una adreça de correu electrònic vàlida.</li>
                <li>Acceptar la recepció de comunicacions comercials de Turris.</li>
                <li>Acceptar la política de privacitat i aquestes condicions d'ús.</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                <strong>Important:</strong> L'acceptació de rebre comunicacions comercials és una condició necessària 
                per obtenir el conte gratuït. Pots donar-te de baixa de les comunicacions en qualsevol moment 
                mitjançant l'enllaç de baixa inclòs a cada correu.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                4. Ús del Contingut
              </h2>
              <p className="mb-4 leading-relaxed">
                El conte generat és per a ús personal i no comercial. Està permès:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Imprimir el conte per a ús personal o familiar.</li>
                <li>Compartir el conte amb familiars i amics.</li>
                <li>Utilitzar el conte amb finalitats educatives no comercials.</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                No està permès:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Vendre o distribuir comercialment el conte.</li>
                <li>Modificar el contingut per atribuir-lo a una altra marca.</li>
                <li>Utilitzar les il·lustracions o textos per a projectes comercials.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                5. Propietat Intel·lectual
              </h2>
              <p className="mb-4 leading-relaxed">
                Tot el contingut del servei, incloent-hi textos, il·lustracions, disseny i codi, és propietat de 
                Turris o dels seus llicenciadors i està protegit per les lleis de propietat intel·lectual.
              </p>
              <p className="mb-4 leading-relaxed">
                La marca "Turris" i el logotip són marques registrades.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                6. Responsabilitat de l'Usuari
              </h2>
              <p className="mb-4 leading-relaxed">
                L'usuari es compromet a:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Proporcionar informació veraç i actualitzada.</li>
                <li>Utilitzar el servei de manera lícita i respectuosa.</li>
                <li>No introduir noms o contingut ofensiu, discriminatori o il·legal.</li>
                <li>No intentar accedir a àrees restringides del sistema.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                7. Limitació de Responsabilitat
              </h2>
              <p className="mb-4 leading-relaxed">
                Turris no es fa responsable de:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Errors tipogràfics o de contingut en els contes generats.</li>
                <li>Problemes tècnics que impedeixin l'accés al servei.</li>
                <li>Pèrdua de dades per causes alienes al nostre control.</li>
                <li>L'ús que l'usuari faci del conte generat.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                8. Comunicacions Comercials
              </h2>
              <p className="mb-4 leading-relaxed">
                En utilitzar el servei i proporcionar el teu correu electrònic, acceptes rebre:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>El conte personalitzat sol·licitat.</li>
                <li>Comunicacions comercials sobre productes i serveis de Turris.</li>
                <li>Novetats, promocions i ofertes especials.</li>
                <li>Informació sobre nous contes i continguts.</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                Pots cancel·lar la subscripció a les comunicacions comercials en qualsevol moment fent clic 
                a l'enllaç de "baixa" que trobaràs al peu de cada correu electrònic que rebis.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                9. Modificació de les Condicions
              </h2>
              <p className="mb-4 leading-relaxed">
                Turris es reserva el dret de modificar aquestes condicions d'ús en qualsevol moment. 
                Les modificacions seran efectives des de la seva publicació en aquesta pàgina.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                10. Llei Aplicable i Jurisdicció
              </h2>
              <p className="mb-4 leading-relaxed">
                Aquestes condicions d'ús es regeixen per la legislació espanyola. Per a qualsevol controvèrsia 
                derivada d'aquestes condicions, les parts se sotmeten als jutjats i tribunals de Barcelona, 
                amb renúncia expressa a qualsevol altre fur que pogués correspondre'ls.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#1a365d' }}>
                11. Contacte
              </h2>
              <p className="mb-4 leading-relaxed">
                Per a qualsevol consulta sobre aquestes condicions d'ús, pots contactar-nos a:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Email: <a href="mailto:atencio.client@turris.es" className="text-blue-600 hover:underline">atencio.client@turris.es</a></li>
              </ul>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-gray-100">
        <p className="text-sm" style={{ color: '#666' }}>
          © 2025 Turris - El Pa de Sant Jordi. Tots els drets reservats.
        </p>
      </footer>
    </div>
  );
}
